import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "../common/Button";

// import Modal from "../common/Modal";

import Modal from "../common/Modal2";

import SubjectForm from "../admin/SubjectForm";
import {
  // getLatestSubjectId,
  getAllSubjects,
  createSubject,
} from "../../services/subjectService";
import TeacherPicker from "../common/TeacherPicker";
import { getAllCourses } from "../../services/courseService";

// Derive a lightweight course code such as CSC123 from the name
const generateCourseCode = (courseName = "") => {
  const trimmed = courseName.replace(/[^a-zA-Z]/g, "").toUpperCase();
  const prefix = trimmed.slice(0, 3);
  if (!prefix) return "";
  const hash = Array.from(courseName).reduce(
    (acc, char, index) => acc + char.charCodeAt(0) * (index + 1),
    0
  );
  const numericPart = String(hash % 1000).padStart(3, "0");
  return `${prefix}${numericPart}`;
};

const CourseForm = ({
  onSubmit,
  onCancel,
  loading,
  initialData = {},
  step,
  preferStoredTeacher = false,
  hideAssignTeacher = false,
}) => {
  // Compute a sensible default academic year (e.g., "2025-2026")
  const computeAcademicYear = () => {
    const now = new Date();
    const y = now.getFullYear();
    return `${y}-${y + 1}`;
  };
  const LOCAL_STORAGE_KEY = "course_selected_classes";
  const isEditing = Boolean(
    initialData?.id ?? initialData?.CourseID ?? initialData?.courseId
  );
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...initialData,
      academicYear: initialData?.academicYear ?? computeAcademicYear(),
    },
  });

  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [subjectsError, setSubjectsError] = useState("");
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [loadingAvailableSubjects, setLoadingAvailableSubjects] =
    useState(false);
  const [creatingSubjectLocal, setCreatingSubjectLocal] = useState(false);
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(
    Boolean(initialData?.code)
  );
  const [autoGeneratedCode, setAutoGeneratedCode] = useState("");
  const courseName = watch("name");
  const [storedTeacher, setStoredTeacher] = useState(null);

  // Auto-fill the Subject ID with the latest from backend if not provided
  // useEffect(() => {
  //   let cancelled = false;
  //   const run = async () => {
  //     if (initialData && initialData.subjectId) return;
  //     try {
  //       const latest = await getLatestSubjectId();
  //       // If backend returns the latest existing SubjectID, show the next available id (latest + 1)
  //       if (!cancelled && typeof latest === "number" && !Number.isNaN(latest)) {
  //         const nextId = latest + 1;
  //         setValue("subjectId", String(nextId), { shouldValidate: true });
  //       }
  //     } catch (_) {
  //       // ignore; user can type manually
  //     }
  //   };
  //   run();
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [initialData, setValue]);

  // Initialize `selectedSubjects` once from `initialData` or localStorage
  useEffect(() => {
    let list = [];

    const normalizeSubjectEntry = (entry) => {
      if (!entry) {
        return { id: null, name: "", isNew: false };
      }

      if (typeof entry === "string") {
        const trimmed = entry.trim();
        return {
          id: null,
          name: trimmed || "",
          isNew: false,
        };
      }

      const draft = entry?.draft;
      const resolvedId =
        entry.id ??
        entry.subjectId ??
        entry.SubjectID ??
        entry.subjectID ??
        draft?.id ??
        draft?.subjectId ??
        draft?.SubjectID ??
        null;

      const resolvedName =
        entry.name ??
        entry.subjectName ??
        entry.SubjectName ??
        entry.title ??
        entry.Title ??
        draft?.name ??
        draft?.subjectName ??
        draft?.SubjectName ??
        "";

      return {
        id: resolvedId,
        name: typeof resolvedName === "string" ? resolvedName : "",
        isNew: Boolean(entry.isNew),
      };
    };

    if (Array.isArray(initialData?.subjects) && initialData.subjects.length) {
      list = initialData.subjects.map((entry) => normalizeSubjectEntry(entry));
    }

    if (!list.length) {
      const singleSubjectName =
        typeof initialData?.subject === "string"
          ? initialData.subject.trim()
          : typeof initialData?.Subject === "string"
          ? initialData.Subject.trim()
          : "";

      if (singleSubjectName) {
        list = [normalizeSubjectEntry(singleSubjectName)];
      }
    }

    if (!isEditing) {
      setSelectedSubjects(list);
      try {
        window.localStorage.removeItem(LOCAL_STORAGE_KEY);
      } catch (e) {
        // ignore storage errors in new-course flow
      }
      return;
    }

    try {
      const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (!list.length && Array.isArray(parsed)) {
          list = parsed;
        }
      }
    } catch (e) {
      // ignore malformed storage
    }

    setSelectedSubjects(list);
  }, []); // run only once on mount

  // If this form instance prefers the stored teacher (Add New Course flow),
  // try to load the teacher info saved by Users -> Edit action.
  useEffect(() => {
    if (!preferStoredTeacher) return;
    try {
      const raw = window.localStorage.getItem("selected_teacher_for_course");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && (parsed.id || parsed.name)) {
          setStoredTeacher({
            id: parsed.id ?? String(parsed.id),
            name: parsed.name ?? "",
          });
        }
      }
    } catch (e) {
      // ignore
    }
  }, [preferStoredTeacher]);

  const loadAvailableSubjects = async () => {
    setLoadingAvailableSubjects(true);
    try {
      const subs = await getAllSubjects();
      setAvailableSubjects(subs || []);
    } catch (err) {
      console.error("Failed to load subjects for picker", err);
      setAvailableSubjects([]);
    } finally {
      setLoadingAvailableSubjects(false);
    }
  };

  useEffect(() => {
    // pre-load subjects so picker is responsive
    loadAvailableSubjects();
  }, []);

  useEffect(() => {
    if (!Array.isArray(availableSubjects) || !availableSubjects.length) {
      return;
    }

    setSelectedSubjects((prev) => {
      if (!Array.isArray(prev) || !prev.length) {
        return prev;
      }

      const normalize = (value) =>
        String(value ?? "")
          .trim()
          .toLowerCase();

      const lookup = new Map();
      for (const subject of availableSubjects) {
        const key = normalize(
          subject?.name ??
            subject?.subjectName ??
            subject?.SubjectName ??
            subject?.title ??
            subject?.Title
        );
        if (!key || lookup.has(key)) continue;
        const idCandidate =
          subject?.id ??
          subject?.SubjectID ??
          subject?.subjectId ??
          subject?.subjectID ??
          null;
        lookup.set(key, {
          id: idCandidate,
          name:
            subject?.name ??
            subject?.subjectName ??
            subject?.SubjectName ??
            subject?.title ??
            subject?.Title ??
            "",
        });
      }

      const primaryId =
        initialData?.subjectId ??
        initialData?.SubjectID ??
        initialData?.subjectID ??
        null;

      let changed = false;
      const next = prev.map((entry, index) => {
        if (!entry || entry.id) return entry;

        const key = normalize(entry.name);
        if (key && lookup.has(key)) {
          const match = lookup.get(key);
          if (match?.id !== undefined && match?.id !== null) {
            changed = true;
            return { ...entry, id: match.id };
          }
        }

        if (index === 0 && primaryId !== null && primaryId !== undefined) {
          changed = true;
          return { ...entry, id: primaryId };
        }

        return entry;
      });

      return changed ? next : prev;
    });
  }, [
    availableSubjects,
    initialData?.subjectId,
    initialData?.SubjectID,
    initialData?.subjectID,
  ]);

  useEffect(() => {
    if (initialData?.code) {
      setCodeManuallyEdited(true);
    }
  }, [initialData?.code]);

  useEffect(() => {
    if (initialData?.code || codeManuallyEdited) return;
    if (!courseName) {
      if (autoGeneratedCode) {
        setAutoGeneratedCode("");
        setValue("code", "", { shouldDirty: true, shouldValidate: true });
      }
      return;
    }

    const generated = generateCourseCode(courseName);
    if (!generated || generated === autoGeneratedCode) return;

    setAutoGeneratedCode(generated);
    setValue("code", generated, { shouldDirty: true, shouldValidate: true });
  }, [
    autoGeneratedCode,
    codeManuallyEdited,
    courseName,
    initialData?.code,
    setValue,
  ]);

  // Async validator to prevent duplicate course codes
  const validateCourseCode = async (val) => {
    try {
      const trimmed = String(val || "")
        .trim()
        .toUpperCase();
      if (!trimmed) return true;
      const existing =
        initialData && (initialData.code || initialData.CourseCode)
          ? String(initialData.code || initialData.CourseCode || "")
              .trim()
              .toUpperCase()
          : null;
      if (existing && existing === trimmed) return true;
      const courses = await getAllCourses();
      const found = (courses || []).find(
        (c) =>
          String(c.code || c.CourseCode || c.courseCode || "")
            .toString()
            .trim()
            .toUpperCase() === trimmed
      );
      if (!found) return true;
      const foundId = found.id ?? found.CourseID ?? found.courseId ?? null;
      const currentId =
        initialData?.id ??
        initialData?.CourseID ??
        initialData?.courseId ??
        null;
      if (currentId && String(foundId) === String(currentId)) return true;
      return "Course code already in use";
    } catch (err) {
      // On API failure, allow submit (do not block UX)
      return true;
    }
  };

  const codeField = register("code", {
    required: "Course code is required",
    pattern: {
      value: /^[A-Za-z0-9_-]{2,20}$/,
      message: "Course code must be 2–20 alphanumeric characters (no spaces)",
    },
    // async uniqueness validator
    validate: validateCourseCode,
  });

  return (
    <form
      onSubmit={handleSubmit((data) => {
        setSubjectsError("");
        // require at least one subject on course creation
        if (!selectedSubjects || selectedSubjects.length === 0) {
          setSubjectsError(
            "At least one class must be selected for the course."
          );
          return;
        }

        // Determine teacher id (prefer form field, fallback to localStorage JSON)
        let rawTeacher =
          data.teacherId ?? data.teacherID ?? data.TeacherID ?? "";
        let teacherId = "";
        if (rawTeacher) {
          teacherId = rawTeacher;
        } else {
          try {
            const stored = window.localStorage.getItem(
              "selected_teacher_for_course"
            );
            if (stored) {
              try {
                const parsed = JSON.parse(stored);
                teacherId = parsed?.id ?? String(parsed) ?? "";
              } catch (err) {
                teacherId = stored;
              }
            }
          } catch (e) {
            // ignore storage errors
          }
        }

        // Prefer first selected subject as the primary SubjectID
        const firstSub =
          selectedSubjects && selectedSubjects.length
            ? selectedSubjects[0]
            : null;
        const subjId = firstSub
          ? firstSub.id ?? data.subjectId ?? data.SubjectID ?? null
          : data.subjectId ?? data.SubjectID ?? null;

        // Build SubjectIDs array (only IDs that are available)
        const subjectIds = (selectedSubjects || [])
          .map((s) => s?.id ?? s?.SubjectID ?? s?.subjectId ?? null)
          .filter((v) => v !== null && v !== undefined)
          .map((v) => (typeof v === "string" ? Number(v) : v));

        const payload = {
          ...data,
          subjects: selectedSubjects,
          // ensure both shapes are present for backend compatibility
          TeacherID: teacherId,
          teacherId: teacherId,
          SubjectID: subjId,
          subjectId: subjId,
          // new API expects SubjectIDs array
          SubjectIDs: subjectIds,
        };

        onSubmit(payload);
      })}
      className="space-y-6"
    >
      {/* Step indicator removed per request - show the form directly */}
      <div>
        <label
          htmlFor="code"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Course Code
        </label>
        <Controller
          name="code"
          control={control}
          render={({ field }) => {
            const displayed =
              (field.value && String(field.value).trim()) ||
              autoGeneratedCode ||
              "";
            return (
              <input
                disabled
                id="code"
                type="text"
                value={displayed}
                onChange={(e) => {
                  const upper = String(e.target.value || "")
                    .toUpperCase()
                    .replace(/\s+/g, "");
                  setCodeManuallyEdited(true);
                  field.onChange(upper);
                }}
                onBlur={async (e) => {
                  const v = String(field.value || displayed || "").trim();
                  if (!v) return;
                  const res = await validateCourseCode(v);
                  if (res !== true) {
                    // rely on react-hook-form errors handling; no-op here
                  }
                }}
                className="mt-1 px-2 py-2 text-xs block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            );
          }}
        />
        {errors.code && (
          <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Course Name
        </label>
        <input
          id="name"
          name="name"
          type="text"
          {...register("name", { required: "Course name is required" })}
          className="mt-1 px-2 py-2 text-sm block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        {!hideAssignTeacher ? (
          <>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Assigned Teacher
            </label>
            <Controller
              name="teacherId"
              control={control}
              render={({ field }) => (
                <TeacherPicker
                  value={field.value}
                  onChange={(val) => field.onChange(val ?? "")}
                  onBlur={field.onBlur}
                  disabled={loading}
                  showRefresh={false}
                />
              )}
            />
            {errors.teacherId && (
              <p className="mt-1 text-sm text-red-600">
                {errors.teacherId.message}
              </p>
            )}
          </>
        ) : null}
      </div>

      

      <div>
        <label
          htmlFor="academicYear"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Academic Year
        </label>
        <input
          id="academicYear"
          name="academicYear"
          type="text"
          {...register("academicYear", {
            required: " is required",
          })}
          className="mt-1 px-2 py-2 text-sm block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
        {errors.academicYear && (
          <p className="mt-1 text-sm text-red-600">
            {errors.academicYear.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Description
        </label>
        <textarea
          id="description"
          name="description"
          rows={3}
          {...register("description")}
          className="mt-1 px-2 py-2 text-sm block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      </div>

      {/* Classes - moved to end of form (display after Description) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Classes
        </label>
        <div className="mt-2 space-y-2">
          {selectedSubjects && selectedSubjects.length ? (
            <ul className="list-disc list-inside">
              {selectedSubjects.map((s, idx) => (
                <li
                  key={`${s.name}-${idx}`}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="text-sm">{s.name}</div>
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        const next = (selectedSubjects || []).filter(
                          (_, i) => i !== idx
                        );
                        setSelectedSubjects(next);
                        try {
                          window.localStorage.setItem(
                            LOCAL_STORAGE_KEY,
                            JSON.stringify(next)
                          );
                        } catch (e) {
                          // ignore storage errors
                        }
                        try {
                          const first = next[0];
                          if (
                            first &&
                            first.id !== null &&
                            first.id !== undefined
                          ) {
                            setValue("subjectId", first.id, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          } else {
                            setValue("subjectId", "", {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                        } catch (err) {
                          // ignore
                        }
                      }}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500">No classes selected.</div>
          )}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center">
            <div className="w-full sm:flex-1">
              <select
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) return;
                  const found = (availableSubjects || []).find(
                    (a) => String(a.id) === String(val)
                  );
                  if (found) {
                    const name =
                      found.name ??
                      found.subjectName ??
                      found.SubjectName ??
                      "";
                    // avoid duplicates by name
                    if (
                      !selectedSubjects.some(
                        (ss) =>
                          String(ss.name).toLowerCase() ===
                          String(name).toLowerCase()
                      )
                    ) {
                      const next = [
                        ...(selectedSubjects || []),
                        { id: found.id ?? null, name, isNew: false },
                      ];
                      setSelectedSubjects(next);
                      try {
                        window.localStorage.setItem(
                          LOCAL_STORAGE_KEY,
                          JSON.stringify(next)
                        );
                      } catch (e) {
                        // ignore storage errors
                      }
                      // also set primary subject id field so API receives SubjectID
                      try {
                        setValue("subjectId", found.id ?? "", {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      } catch (err) {
                        // ignore
                      }
                    }
                  }
                  // reset select
                  e.target.value = "";
                }}
                className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                disabled={loadingAvailableSubjects}
              >
                <option value="">-- Choose existing class --</option>
                {(availableSubjects || []).map((s) => {
                  const name = s.name ?? s.subjectName ?? s.SubjectName ?? "";
                  return (
                    <option key={String(s.id)} value={String(s.id)}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="sm:flex-shrink-0">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowSubjectModal(true)}
                className="w-full justify-center sm:w-auto"
              >
                + New
              </Button>
            </div>
          </div>
          {subjectsError ? (
            <p className="mt-1 text-sm text-red-600">{subjectsError}</p>
          ) : null}
          {showSubjectModal && (
            <Modal
              isOpen={showSubjectModal}
              onClose={() => setShowSubjectModal(false)}
              title="Add new class"
              size="md"
            >
              <SubjectForm
                onSubmit={async (data) => {
                  // Persist the new subject immediately so we have an id to send with the course
                  setCreatingSubjectLocal(true);
                  try {
                    const payload = {
                      ...(data || {}),
                      ...(initialData?.name
                        ? { courseName: initialData.name }
                        : {}),
                    };
                    const created = await createSubject(payload);
                    const name =
                      created?.name ??
                      created?.subjectName ??
                      created?.SubjectName ??
                      data.name ??
                      "";
                    const id =
                      created?.id ??
                      created?.SubjectID ??
                      created?.subjectId ??
                      null;
                    {
                      const next = [
                        ...(selectedSubjects || []),
                        { id: id, name, isNew: false, draft: created },
                      ];
                      setSelectedSubjects(next);
                      try {
                        window.localStorage.setItem(
                          LOCAL_STORAGE_KEY,
                          JSON.stringify(next)
                        );
                      } catch (e) {
                        // ignore storage errors
                      }
                    }
                    if (id !== null && id !== undefined) {
                      try {
                        setValue("subjectId", id, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      } catch (e) {
                        // ignore
                      }
                    }
                    setShowSubjectModal(false);
                  } catch (err) {
                    console.error("Failed to create subject inline:", err);
                  } finally {
                    setCreatingSubjectLocal(false);
                  }
                }}
                onCancel={() => setShowSubjectModal(false)}
                initial={{ courseName: initialData?.name }}
              />
            </Modal>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
        {onCancel ? (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
            className="w-full justify-center sm:w-auto"
          >
            Cancel
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="w-full justify-center sm:w-auto"
        >
          {loading ? "Saving..." : "Save Course"}
        </Button>
      </div>
    </form>
  );
};

export default CourseForm;

// import { useEffect, useState } from "react";
// import { Controller, useForm } from "react-hook-form";
// import Button from "../common/Button";
// import Modal from "../common/Modal";
// import SubjectForm from "../admin/SubjectForm";
// import {
//   getAllSubjects,
//   createSubject,
// } from "../../services/subjectService";
// import TeacherPicker from "../common/TeacherPicker";
// import { getAllCourses } from "../../services/courseService";

// // Derive a lightweight course code such as CSC123 from the name
// const generateCourseCode = (courseName = "") => {
//   const trimmed = courseName.replace(/[^a-zA-Z]/g, "").toUpperCase();
//   const prefix = trimmed.slice(0, 3);
//   if (!prefix) return "";
//   const hash = Array.from(courseName).reduce(
//     (acc, char, index) => acc + char.charCodeAt(0) * (index + 1),
//     0
//   );
//   const numericPart = String(hash % 1000).padStart(3, "0");
//   return `${prefix}${numericPart}`;
// };

// const CourseForm = ({
//   onSubmit,
//   onCancel,
//   loading,
//   initialData = {},
//   step,
// }) => {
//   const {
//     register,
//     handleSubmit,
//     control,
//     setValue,
//     watch,
//     formState: { errors },
//   } = useForm({
//     defaultValues: initialData,
//   });

//   const [selectedSubjects, setSelectedSubjects] = useState([]);
//   const [subjectsError, setSubjectsError] = useState("");
//   const [showSubjectModal, setShowSubjectModal] = useState(false);
//   const [availableSubjects, setAvailableSubjects] = useState([]);
//   const [loadingAvailableSubjects, setLoadingAvailableSubjects] =
//     useState(false);
//   const [creatingSubjectLocal, setCreatingSubjectLocal] = useState(false);
//   const [codeManuallyEdited, setCodeManuallyEdited] = useState(
//     Boolean(initialData?.code)
//   );
//   const [autoGeneratedCode, setAutoGeneratedCode] = useState("");
//   const courseName = watch("name");

//   // Initialize selectedSubjects from initialData if present
//   useEffect(() => {
//     const list = [];
//     if (Array.isArray(initialData?.subjects) && initialData.subjects.length) {
//       for (const s of initialData.subjects) {
//         if (typeof s === "string") {
//           list.push({ id: null, name: s, isNew: false });
//         } else if (s && typeof s === "object") {
//           list.push({
//             id: s.id ?? null,
//             name: s.name ?? s.subjectName ?? s.SubjectName ?? "",
//             isNew: false,
//           });
//         }
//       }
//     } else if (initialData?.subject) {
//       list.push({
//         id: initialData.subjectId ?? null,
//         name: initialData.subject,
//         isNew: false,
//       });
//     } else if (initialData?.subjectId && initialData?.subjectName) {
//       list.push({
//         id: initialData.subjectId,
//         name: initialData.subjectName,
//         isNew: false,
//       });
//     }
//     setSelectedSubjects(list);
//   }, [initialData]);

//   const loadAvailableSubjects = async () => {
//     setLoadingAvailableSubjects(true);
//     try {
//       const subs = await getAllSubjects();
//       setAvailableSubjects(subs || []);
//     } catch (err) {
//       console.error("Failed to load subjects for picker", err);
//       setAvailableSubjects([]);
//     } finally {
//       setLoadingAvailableSubjects(false);
//     }
//   };

//   useEffect(() => {
//     loadAvailableSubjects();
//   }, []);

//   useEffect(() => {
//     if (!Array.isArray(availableSubjects) || !availableSubjects.length) {
//       return;
//     }

//     setSelectedSubjects((prev) => {
//       if (!Array.isArray(prev) || !prev.length) {
//         return prev;
//       }

//       const normalize = (value) =>
//         String(value ?? "")
//           .trim()
//           .toLowerCase();

//       const lookup = new Map();
//       for (const subject of availableSubjects) {
//         const key = normalize(
//           subject?.name ??
//             subject?.subjectName ??
//             subject?.SubjectName ??
//             subject?.title ??
//             subject?.Title
//         );
//         if (!key || lookup.has(key)) continue;
//         const idCandidate =
//           subject?.id ??
//           subject?.SubjectID ??
//           subject?.subjectId ??
//           subject?.subjectID ??
//           null;
//         lookup.set(key, {
//           id: idCandidate,
//           name:
//             subject?.name ??
//             subject?.subjectName ??
//             subject?.SubjectName ??
//             subject?.title ??
//             subject?.Title ??
//             "",
//         });
//       }

//       const primaryId =
//         initialData?.subjectId ??
//         initialData?.SubjectID ??
//         initialData?.subjectID ??
//         null;

//       let changed = false;
//       const next = prev.map((entry, index) => {
//         if (!entry || entry.id) return entry;

//         const key = normalize(entry.name);
//         if (key && lookup.has(key)) {
//           const match = lookup.get(key);
//           if (match?.id !== undefined && match?.id !== null) {
//             changed = true;
//             return { ...entry, id: match.id };
//           }
//         }

//         if (index === 0 && primaryId !== null && primaryId !== undefined) {
//           changed = true;
//           return { ...entry, id: primaryId };
//         }

//         return entry;
//       });

//       return changed ? next : prev;
//     });
//   }, [
//     availableSubjects,
//     initialData?.subjectId,
//     initialData?.SubjectID,
//     initialData?.subjectID,
//   ]);

//   useEffect(() => {
//     if (initialData?.code) {
//       setCodeManuallyEdited(true);
//     }
//   }, [initialData?.code]);

//   useEffect(() => {
//     if (initialData?.code || codeManuallyEdited) return;
//     if (!courseName) {
//       if (autoGeneratedCode) {
//         setAutoGeneratedCode("");
//         setValue("code", "", { shouldDirty: true, shouldValidate: true });
//       }
//       return;
//     }

//     const generated = generateCourseCode(courseName);
//     if (!generated || generated === autoGeneratedCode) return;

//     setAutoGeneratedCode(generated);
//     setValue("code", generated, { shouldDirty: true, shouldValidate: true });
//   }, [
//     autoGeneratedCode,
//     codeManuallyEdited,
//     courseName,
//     initialData?.code,
//     setValue,
//   ]);

//   // Async validator to prevent duplicate course codes
//   const validateCourseCode = async (val) => {
//     try {
//       const trimmed = String(val || "")
//         .trim()
//         .toUpperCase();
//       if (!trimmed) return true;
//       const existing =
//         initialData && (initialData.code || initialData.CourseCode)
//           ? String(initialData.code || initialData.CourseCode || "")
//               .trim()
//               .toUpperCase()
//           : null;
//       if (existing && existing === trimmed) return true;
//       const courses = await getAllCourses();
//       const found = (courses || []).find(
//         (c) =>
//           String(c.code || c.CourseCode || c.courseCode || "")
//             .toString()
//             .trim()
//             .toUpperCase() === trimmed
//       );
//       if (!found) return true;
//       const foundId = found.id ?? found.CourseID ?? found.courseId ?? null;
//       const currentId =
//         initialData?.id ??
//         initialData?.CourseID ??
//         initialData?.courseId ??
//         null;
//       if (currentId && String(foundId) === String(currentId)) return true;
//       return "Course code already in use";
//     } catch (err) {
//       return true;
//     }
//   };

//   const codeField = register("code", {
//     required: "Course code is required",
//     pattern: {
//       value: /^[A-Za-z0-9_-]{2,20}$/,
//       message: "Course code must be 2–20 alphanumeric characters (no spaces)",
//     },
//     validate: validateCourseCode,
//   });

//   return (
//     <div className="min-h-full bg-gray-50 dark:bg-gray-900 py-6">
//       <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
//           <div className="mb-8">
//             <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
//               {initialData?.id ? "Edit Course" : "Create New Course"}
//             </h1>
//             <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
//               Fill in the course details below to {initialData?.id ? "update" : "create"} the course.
//             </p>
//           </div>

//           <form
//             onSubmit={handleSubmit((data) => {
//               setSubjectsError("");
//               if (!selectedSubjects || selectedSubjects.length === 0) {
//                 setSubjectsError(
//                   "At least one class must be selected for the course."
//                 );
//                 return;
//               }

//               const rawTeacher =
//                 data.teacherId ?? data.teacherID ?? data.TeacherID ?? "";
//               const teacherId =
//                 rawTeacher === null || rawTeacher === undefined ? "" : rawTeacher;

//               const firstSub =
//                 selectedSubjects && selectedSubjects.length
//                   ? selectedSubjects[0]
//                   : null;
//               const subjId = firstSub
//                 ? firstSub.id ?? data.subjectId ?? data.SubjectID ?? null
//                 : data.subjectId ?? data.SubjectID ?? null;

//               const subjectIds = (selectedSubjects || [])
//                 .map((s) => s?.id ?? s?.SubjectID ?? s?.subjectId ?? null)
//                 .filter((v) => v !== null && v !== undefined)
//                 .map((v) => (typeof v === "string" ? Number(v) : v));

//               const payload = {
//                 ...data,
//                 subjects: selectedSubjects,
//                 TeacherID: teacherId,
//                 teacherId: teacherId,
//                 SubjectID: subjId,
//                 subjectId: subjId,
//                 SubjectIDs: subjectIds,
//               };

//               onSubmit(payload);
//             })}
//             className="space-y-6"
//           >
//             {/* Course Code Field */}
//             <div className="space-y-2">
//               <div className="flex items-center justify-between">
//                 <label
//                   htmlFor="code"
//                   className="block text-sm font-medium text-gray-700 dark:text-gray-300"
//                 >
//                   Course Code
//                 </label>
//                 {!codeManuallyEdited && autoGeneratedCode && (
//                   <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full">
//                     Auto-generated
//                   </span>
//                 )}
//               </div>
//               <div className="relative">
//                 <input
//                   disabled
//                   id="code"
//                   name="code"
//                   type="text"
//                   {...codeField}
//                   onChange={(e) => {
//                     const upper = String(e.target.value || "")
//                       .toUpperCase()
//                       .replace(/\s+/g, "");
//                     setCodeManuallyEdited(true);
//                     codeField.onChange({ target: { name: "code", value: upper } });
//                   }}
//                   onBlur={async (e) => {
//                     const v = String(e.target.value || "").trim();
//                     if (!v) return;
//                     await validateCourseCode(v);
//                   }}
//                   className="block w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 transition-colors duration-200 font-mono text-lg font-medium"
//                   placeholder="e.g., CSC101"
//                 />
//                 <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
//                   <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
//                   </svg>
//                 </div>
//               </div>
//               {errors.code && (
//                 <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
//                   <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
//                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
//                   </svg>
//                   {errors.code.message}
//                 </p>
//               )}
//             </div>

//             {/* Course Name Field */}
//             <div className="space-y-2">
//               <label
//                 htmlFor="name"
//                 className="block text-sm font-medium text-gray-700 dark:text-gray-300"
//               >
//                 Course Name
//               </label>
//               <input
//                 id="name"
//                 name="name"
//                 type="text"
//                 {...register("name", { required: "Course name is required" })}
//                 className="block w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 transition-colors duration-200"
//                 placeholder="Enter course name"
//               />
//               {errors.name && (
//                 <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
//                   <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
//                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
//                   </svg>
//                   {errors.name.message}
//                 </p>
//               )}
//             </div>

//             {/* Teacher Picker */}
//             <div className="space-y-2">
//               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
//                 Assigned Teacher
//               </label>
//               <div className="rounded-xl border border-gray-300 dark:border-gray-600 overflow-hidden">
//                 <Controller
//                   name="teacherId"
//                   control={control}
//                   render={({ field }) => (
//                     <TeacherPicker
//                       value={field.value}
//                       onChange={(val) => field.onChange(val ?? "")}
//                       onBlur={field.onBlur}
//                       disabled={loading}
//                       showRefresh={false}
//                     />
//                   )}
//                 />
//               </div>
//               {errors.teacherId && (
//                 <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
//                   <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
//                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
//                   </svg>
//                   {errors.teacherId.message}
//                 </p>
//               )}
//             </div>

//             {/* Classes Section */}
//             <div className="space-y-4">
//               <div className="flex items-center justify-between">
//                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
//                   Classes
//                 </label>
//                 <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
//                   {selectedSubjects.length} selected
//                 </span>
//               </div>

//               {/* Selected Classes */}
//               <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
//                 {selectedSubjects && selectedSubjects.length ? (
//                   <div className="space-y-2">
//                     {selectedSubjects.map((s, idx) => (
//                       <div
//                         key={`${s.name}-${idx}`}
//                         className="flex items-center justify-between p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-400 transition-colors duration-200"
//                       >
//                         <div className="flex items-center gap-3">
//                           <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
//                           <span className="text-sm font-medium text-gray-900 dark:text-white">
//                             {s.name}
//                           </span>
//                         </div>
//                         <button
//                           type="button"
//                           onClick={() =>
//                             setSelectedSubjects((prev) =>
//                               prev.filter((_, i) => i !== idx)
//                             )
//                           }
//                           className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
//                         >
//                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                           </svg>
//                         </button>
//                       </div>
//                     ))}
//                   </div>
//                 ) : (
//                   <div className="text-center py-6">
//                     <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
//                     </svg>
//                     <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No classes selected</p>
//                     <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add classes to get started</p>
//                   </div>
//                 )}
//               </div>

//               {/* Add Classes Controls */}
//               <div className="flex flex-col gap-3 sm:flex-row">
//                 <div className="flex-1">
//                   <select
//                     onChange={(e) => {
//                       const val = e.target.value;
//                       if (!val) return;
//                       const found = (availableSubjects || []).find(
//                         (a) => String(a.id) === String(val)
//                       );
//                       if (found) {
//                         const name =
//                           found.name ??
//                           found.subjectName ??
//                           found.SubjectName ??
//                           "";
//                         if (
//                           !selectedSubjects.some(
//                             (ss) =>
//                               String(ss.name).toLowerCase() ===
//                               String(name).toLowerCase()
//                           )
//                         ) {
//                           setSelectedSubjects((prev) => [
//                             ...prev,
//                             { id: found.id ?? null, name, isNew: false },
//                           ]);
//                           try {
//                             setValue("subjectId", found.id ?? "", {
//                               shouldDirty: true,
//                               shouldValidate: true,
//                             });
//                           } catch (err) {}
//                         }
//                       }
//                       e.target.value = "";
//                     }}
//                     className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 transition-colors duration-200"
//                     disabled={loadingAvailableSubjects}
//                   >
//                     <option value="">Choose existing class...</option>
//                     {(availableSubjects || []).map((s) => {
//                       const name = s.name ?? s.subjectName ?? s.SubjectName ?? "";
//                       return (
//                         <option key={String(s.id)} value={String(s.id)}>
//                           {name}
//                         </option>
//                       );
//                     })}
//                   </select>
//                 </div>

//                 <Button
//                   type="button"
//                   variant="secondary"
//                   onClick={() => setShowSubjectModal(true)}
//                   className="sm:w-auto justify-center gap-2"
//                 >
//                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
//                   </svg>
//                   New Class
//                 </Button>
//               </div>

//               {subjectsError && (
//                 <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
//                   <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
//                     <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
//                       <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
//                     </svg>
//                     {subjectsError}
//                   </p>
//                 </div>
//               )}
//             </div>

//             {/* Academic Year */}
//             <div className="space-y-2">
//               <label
//                 htmlFor="academicYear"
//                 className="block text-sm font-medium text-gray-700 dark:text-gray-300"
//               >
//                 Academic Year
//               </label>
//               <input
//                 id="academicYear"
//                 name="academicYear"
//                 type="text"
//                 {...register("academicYear", {
//                   required: "Academic year is required",
//                 })}
//                 className="block w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 transition-colors duration-200"
//                 placeholder="e.g., 2024-2025"
//               />
//               {errors.academicYear && (
//                 <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
//                   <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
//                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
//                   </svg>
//                   {errors.academicYear.message}
//                 </p>
//               )}
//             </div>

//             {/* Description */}
//             <div className="space-y-2">
//               <label
//                 htmlFor="description"
//                 className="block text-sm font-medium text-gray-700 dark:text-gray-300"
//               >
//                 Description
//               </label>
//               <textarea
//                 id="description"
//                 name="description"
//                 rows={4}
//                 {...register("description")}
//                 className="block w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 transition-colors duration-200 resize-none"
//                 placeholder="Enter course description (optional)"
//               />
//             </div>

//             {/* Action Buttons */}
//             <div className="flex flex-col gap-3 sm:flex-row sm:justify-end sm:gap-3 pt-6 border-t border-gray-200 dark:border-gray-600">
//               {onCancel && (
//                 <Button
//                   type="button"
//                   variant="secondary"
//                   onClick={onCancel}
//                   disabled={loading}
//                   className="w-full justify-center sm:w-auto order-2 sm:order-1"
//                 >
//                   Cancel
//                 </Button>
//               )}
//               <Button
//                 type="submit"
//                 variant="primary"
//                 disabled={loading}
//                 className="w-full justify-center sm:w-auto order-1 sm:order-2 gap-2"
//               >
//                 {loading ? (
//                   <>
//                     <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v4m0 12v4m8-10h-4M6 12H2" />
//                     </svg>
//                     Saving...
//                   </>
//                 ) : (
//                   <>
//                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                     </svg>
//                     {initialData?.id ? "Update Course" : "Create Course"}
//                   </>
//                 )}
//               </Button>
//             </div>
//           </form>
//         </div>
//       </div>

//       {/* Subject Modal */}
//       {showSubjectModal && (
//         <Modal
//           isOpen={showSubjectModal}
//           onClose={() => setShowSubjectModal(false)}
//           title="Add New Class"
//           size="md"
//         >
//           <SubjectForm
//             onSubmit={async (data) => {
//               setCreatingSubjectLocal(true);
//               try {
//                 const payload = {
//                   ...(data || {}),
//                   ...(initialData?.name
//                     ? { courseName: initialData.name }
//                     : {}),
//                 };
//                 const created = await createSubject(payload);
//                 const name =
//                   created?.name ??
//                   created?.subjectName ??
//                   created?.SubjectName ??
//                   data.name ??
//                   "";
//                 const id =
//                   created?.id ??
//                   created?.SubjectID ??
//                   created?.subjectId ??
//                   null;
//                 setSelectedSubjects((prev) => [
//                   ...prev,
//                   { id: id, name, isNew: false, draft: created },
//                 ]);
//                 if (id !== null && id !== undefined) {
//                   try {
//                     setValue("subjectId", id, {
//                       shouldDirty: true,
//                       shouldValidate: true,
//                     });
//                   } catch (e) {}
//                 }
//                 setShowSubjectModal(false);
//               } catch (err) {
//                 console.error("Failed to create subject inline:", err);
//               } finally {
//                 setCreatingSubjectLocal(false);
//               }
//             }}
//             onCancel={() => setShowSubjectModal(false)}
//             initial={{ courseName: initialData?.name }}
//           />
//         </Modal>
//       )}
//     </div>
//   );
// };

// export default CourseForm;
